import type { DataFlowStep } from "./types";

export type NodeTypeKey =
  "download" | "upload" | "transformation" | "bqQuery" | "processing" | "table" | "destinationTable" | "bundleTable";

export type DataPaletteToken =
  "dataPeach" | "dataCream" | "dataSky" | "dataGinger" | "dataArctic" | "dataCerulean" | "dataLime" | "dataMaroon";

export interface NodeTypeMeta {
  label: string;
  paletteToken: DataPaletteToken;
  shape: "rect" | "ellipse";
  // Mode-neutral text color. The data-palette bg tokens (dataPeach etc.) don't
  // change between light/dark mode, so pairing them with text10/text50 (which
  // do flip) breaks contrast in one mode. Hardcoded hexes here keep contrast
  // consistent across both modes.
  textColor: string;
}

export const NODE_TYPES: Record<NodeTypeKey, NodeTypeMeta> = {
  download: { label: "Download", paletteToken: "dataPeach", shape: "rect", textColor: "#1C1C1C" },
  upload: { label: "Upload", paletteToken: "dataCream", shape: "rect", textColor: "#1C1C1C" },
  transformation: { label: "Transformation", paletteToken: "dataSky", shape: "rect", textColor: "#1C1C1C" },
  bqQuery: { label: "BigQuery query", paletteToken: "dataGinger", shape: "rect", textColor: "#FFFFFF" },
  processing: {
    label: "Processing (BQ create/replace)",
    paletteToken: "dataMaroon",
    shape: "rect",
    textColor: "#FFFFFF",
  },
  table: { label: "Table", paletteToken: "dataArctic", shape: "ellipse", textColor: "#FFFFFF" },
  destinationTable: {
    label: "Table created from download",
    paletteToken: "dataCerulean",
    shape: "ellipse",
    textColor: "#FFFFFF",
  },
  bundleTable: { label: "Bundle table", paletteToken: "dataLime", shape: "ellipse", textColor: "#FFFFFF" },
};

export const LEGEND_ORDER: NodeTypeKey[] = [
  "download",
  "bundleTable",
  "upload",
  "transformation",
  "bqQuery",
  "processing",
  "table",
  "destinationTable",
];

// The GraphQL StepKind reaches the client as the enum *name* ("BIGQUERY_QUERY"), not its value
// ("bigquery:query"). Match both so the check is robust to either representation.
export function isBqQueryStepKind(stepKind: string | undefined): boolean {
  return stepKind === "BIGQUERY_QUERY" || stepKind === "bigquery:query";
}

export function getNodeTypeKey(
  node: Pick<DataFlowStep, "kind" | "isDestTable" | "stepKind" | "hasDestTables">
): NodeTypeKey | null {
  if (node.kind === "BundleTable") return "bundleTable";
  if (node.hasDestTables) return "processing";
  if (isBqQueryStepKind(node.stepKind)) return "bqQuery";
  const k = node.kind.toLowerCase();
  if (k === "source") return "download";
  if (k === "sink") return "upload";
  if (k === "transformation") return "transformation";
  if (k === "table") return node.isDestTable ? "destinationTable" : "table";
  return null;
}
